export interface QboBannerStatus {
	/** False when this deployment has no QuickBooks credentials at all. */
	configured: boolean;
	needsReauth: boolean;
	pendingCustomerCount: number;
	lastRunId: string | null;
	lastRunStatus: "success" | "failed" | null;
}

export type QboBannerKind = "reauth" | "sync_failed" | "stale_customers";

export interface QboBanner {
	kind: QboBannerKind;
	tone: "urgent" | "warning" | "info";
	message: string;
	ctaLabel: string;
	ctaHref: string;
	/**
	 * Identity of *this* piece of news. Dismissal is stored against it, so a
	 * later run with fresh changes shows up again instead of being swallowed
	 * by an earlier dismissal.
	 */
	dismissKey: string;
}

/**
 * Decide which QuickBooks banner (if any) belongs at the top of the app.
 *
 * At most one shows at a time, most-blocking first: a disconnected
 * integration stops invoicing outright, a failed sync means the customer list
 * may be stale, and pending changes are merely waiting on a decision.
 */
export function qboBannerFor(
	status: QboBannerStatus,
	dismissed: ReadonlySet<string>,
): QboBanner | null {
	if (!status.configured) return null;

	const candidate = pick(status);
	if (!candidate) return null;
	if (dismissed.has(candidate.dismissKey)) return null;
	return candidate;
}

function pick(status: QboBannerStatus): QboBanner | null {
	if (status.needsReauth) {
		return {
			kind: "reauth",
			tone: "urgent",
			message: "QuickBooks is disconnected. Invoices cannot be sent until you reconnect.",
			ctaLabel: "Reconnect QuickBooks",
			ctaHref: "/settings/quickbooks",
			// No run id: the connection being down is one ongoing condition,
			// not a series of events. Reconnecting is what clears it.
			dismissKey: "qbo-banner:reauth",
		};
	}

	if (status.lastRunStatus === "failed") {
		return {
			kind: "sync_failed",
			tone: "warning",
			message: "Last night's QuickBooks sync did not finish. Customer data may be out of date.",
			ctaLabel: "Open QuickBooks settings",
			ctaHref: "/settings/quickbooks",
			dismissKey: `qbo-banner:sync-failed:${status.lastRunId ?? "unknown"}`,
		};
	}

	if (status.pendingCustomerCount > 0) {
		const n = status.pendingCustomerCount;
		return {
			kind: "stale_customers",
			tone: "info",
			message: `${n} QuickBooks customer ${n === 1 ? "change needs" : "changes need"} a look.`,
			ctaLabel: "Review changes",
			ctaHref: "/settings/customer-mapping",
			dismissKey: `qbo-banner:customers:${status.lastRunId ?? "unknown"}`,
		};
	}

	return null;
}
